import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { company } from '~/company/Company';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import { CONTENT_ID } from '~/pages/Root/constants';
import { mainStore } from '~/stores';

import { HeaderAddressWrapper } from './components/HeaderAddressWrapper';
import { HeaderFavs } from './components/HeaderFavs';
import { HeaderOrders } from './components/HeaderOrders';
import { VIPExpressBanner } from './components/VIPExpressBanner';
import styles from './MobileHeaderCD.module.scss';

type MobileHeaderCDProps = Omit<
  React.ComponentPropsWithRef<'header'>,
  'children'
>;

const MobileHeaderCD = React.forwardRef<
  HTMLHeadingElement,
  MobileHeaderCDProps
>((props, ref) => {
  const [transitionRunning, setTransitionRunning] = useState(false);

  const isOverlayExists = mainStore.isOverlayExists();

  const overlayExistsValueRef = useRef<Nullable<boolean>>(null);

  // Control status bar color based on scroll position
  useStatusBarControl({
    initialColor: 'light-content',
    scrolledColor: 'dark-content',
  });

  const onTransitionEnd = () => {
    setTransitionRunning(false);
  };

  useEffect(() => {
    if (isOverlayExists !== overlayExistsValueRef.current) {
      if (overlayExistsValueRef.current !== null) {
        setTransitionRunning(true);
      }

      overlayExistsValueRef.current = isOverlayExists;
    }

    const className = `${styles.noScroll}`;

    const cleanUp = () => {
      if (document.body.classList.contains(className)) {
        document.body.classList.remove(className);
      }
    };

    if (isOverlayExists) {
      const scrollElement = document.getElementById(CONTENT_ID);
      if (scrollElement && scrollElement.scrollTop > 0) {
        scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (!document.body.classList.contains(className)) {
        document.body.classList.add(className);
      }
    } else {
      cleanUp();
    }

    return cleanUp;
  }, [isOverlayExists]);

  return (
    <header {...props} ref={ref}>
      <div
        className={classes([
          styles.root,
          (isOverlayExists || transitionRunning) && styles.pullToFront,
        ])}
      >
        <div className={styles.root__inner}>
          <HeaderOrders onTransitionEnd={onTransitionEnd} />

          <HeaderAddressWrapper />

          <HeaderFavs onTransitionEnd={onTransitionEnd} />
        </div>
      </div>
      {company.showHeaderExpressBanner && <VIPExpressBanner />}
    </header>
  );
});
MobileHeaderCD.displayName = 'MobileHeaderCD';

export default observer(MobileHeaderCD);
